import React, { useState } from "react";
import { ShareApplicationPerson } from "@/types/ShareApplication";
import { PrimaryButton, ReadOnlyField } from "@ucl/library";
import { useRenewInvite } from "../useRenewInvite";
import { InviteEmailButton } from "../InviteEmailButton";
import { CopyUrlComponent } from "../CopyUrlComponent";
import "./InviteExpiredAccountComponent.scss";

export interface InviteExpiredAccountComponentProps {
  person: ShareApplicationPerson;
  financialApplicationId?: string;
}

export const InviteExpiredAccountComponent: React.FC<InviteExpiredAccountComponentProps> = ({
  person,
  financialApplicationId,
}) => {
  const { renewInvite, isRenewing, error } = useRenewInvite();
  const [renewedInviteLink, setRenewedInviteLink] = useState<string | null>(null);
  const [renewedInviteId, setRenewedInviteId] = useState<string | null>(null);

  const handleRenewInvite = async () => {
    if (!person.validInviteId) {
      console.error("No valid invite ID found");
      return;
    }

    if (!person.isEmailValid) {
      console.error("Invalid email address");
      return;
    }

    try {
      const response = await renewInvite(person.validInviteId);
      setRenewedInviteLink(response.inviteLink);
      setRenewedInviteId(response.inviteId);
    } catch (err) {
      // Error is already handled in the hook
      console.error("Failed to renew invite:", err);
    }
  };

  // Get tooltip text for renew button
  const getRenewButtonTooltip = (): string | undefined => {
    if (!person.validInviteId) {
      return "No valid invite ID available";
    }
    if (!person.isEmailValid) {
      return "Invalid email address format";
    }
    return undefined;
  };

  return (
    <div className="invite-expired-account-component">
      <h3>Share Application with {person.fullName}</h3>
      <p>
        The previous invite has expired. You can renew the invite to generate a new link and send a fresh invitation.
      </p>

      <div className="invite-expired-account-component__email-section">
        <ReadOnlyField
          value={person.emailAddress}
          label="Email Address"
          description="The email address where the invite will be sent"
          minimal={false}
        />
      </div>

      {error && (
        <div className="invite-expired-account-component__error">
          <p>Error: {error}</p>
        </div>
      )}

      {renewedInviteLink ? (
        <div className="invite-expired-account-component__success">
          <h4>Invite Renewed Successfully!</h4>
          <p>The invite has been renewed and is now valid for 7 days.</p>
          
          <CopyUrlComponent
            url={renewedInviteLink}
            label="New Invite Link"
            description="Copy this link to share with the person"
          />

          <div className="invite-expired-account-component__send-email-section">
            <InviteEmailButton
              person={{
                ...person,
                validInviteId: renewedInviteId || person.validInviteId,
              }}
              financialApplicationId={financialApplicationId}
              buttonText="Send Invite Email"
              isReSend={false}
            />
          </div>
        </div>
      ) : (
        <div className="invite-expired-account-component__renew-section">
          <PrimaryButton
            text={isRenewing ? "Renewing..." : "Renew Invite"}
            onClick={handleRenewInvite}
            disabled={isRenewing || !person.validInviteId || !person.isEmailValid}
            title={getRenewButtonTooltip()}
          />
        </div>
      )}
    </div>
  );
};

export default InviteExpiredAccountComponent;
